import { useRef, useEffect, useState } from 'react';
import {
    useCarBySlug,
    useLogEntries,
    useGallery,
    useNotes,
    useSpareParts,
    useLocations,
    useReminders,
} from '../../hooks/useCarData';
import { CarOverview } from '../../views/CarOverview';
import { NavigationBar } from '../ui/NavigationBar';
import { Menu } from 'lucide-react';

interface CarLandingPreviewProps {
    slug: string;
}

// Virtual render width — larger value = smaller-looking fonts/UI (but still edge-to-edge)
// 375 = real iPhone width (normal size); 430 = ~13% smaller appearance
const RENDER_WIDTH = 430;

/**
 * Renders the real car dashboard (CarOverview) inside the landing page phone
 * mockup frame — without an iframe. Uses:
 *  - The same React instance & libraries (no memory duplication)
 *  - TanStack Query cache (data shared with any live session)
 *  - ResizeObserver for responsive scale
 *  - pointer-events: none so the user can't interact with it
 *
 * RAM comparison:
 *  - iframe  → full new browser context = ~50-100 MB per iframe
 *  - this    → just component state + TanStack Query data ≈ 5-10 MB total
 */
export function CarLandingPreview({ slug }: CarLandingPreviewProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(0.71); // default for 320px phone frame (304/430)

    // Fetch data using the same hooks as the real CarDashboard
    const { data: car, isLoading: carLoading } = useCarBySlug(slug);
    const { data: logEntries = [] } = useLogEntries(car?.id);
    const { data: galleryRaw = [] } = useGallery(car?.id);
    const { data: notes = [] } = useNotes(car?.id);
    const { data: parts = [] } = useSpareParts(car?.id);
    const { data: locations = [] } = useLocations(car?.id);
    const { data: reminders = [] } = useReminders(car?.id);

    // Dynamically compute scale so the 375px-wide render fits any phone frame size
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const ro = new ResizeObserver(entries => {
            const w = entries[0]?.contentRect.width;
            if (w > 0) setScale(w / RENDER_WIDTH);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const galleryImages = galleryRaw.map(img => ({
        url: img.image_url,
        thumbnail: img.image_url_thumb || img.image_url,
        comment: img.comment,
        type: img.type,
        expense_id: (img as any).expense_id,
        spare_part_id: (img as any).spare_part_id,
    }));

    return (
        <div ref={containerRef} className="w-full h-full overflow-hidden relative">
            {!car || carLoading ? (
                // Loading state — same spinner as the real app
                <div className="w-full h-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400" />
                </div>
            ) : (
                /*
                 * Scaling wrapper:
                 *  - Fixed width at RENDER_WIDTH so the layout behaves like a real phone
                 *  - CSS transform scales it to fit the actual phone frame
                 *  - pointer-events: none → no interaction
                 *  - The parent overflow: hidden clips anything that overflows vertically
                 */
                <div
                    style={{
                        width: `${RENDER_WIDTH}px`,
                        transformOrigin: 'top left',
                        transform: `scale(${scale})`,
                        pointerEvents: 'none',
                        userSelect: 'none',
                    }}
                >
                    {/* Matches what CarDashboard renders for a public/preview view */}
                    <NavigationBar
                        title=""
                        leftAction={{
                            icon: Menu,
                            onClick: () => {},
                            className: 'text-blue-600 dark:text-blue-400',
                        }}
                        fixed={false}
                    />
                    <CarOverview
                        car={car}
                        logEntries={logEntries}
                        loading={false}
                        galleryImages={galleryImages}
                        notes={notes}
                        parts={parts}
                        locations={locations}
                        reminders={reminders}
                        isPublicView={true}
                        isPreview={true}
                        onTabChange={() => {}} // no-op: preview is read-only
                    />
                </div>
            )}
        </div>
    );
}
